<template>
  <div class="layout-shell" @keydown.esc="siderCollapsed = true">
    <PageHeader :hasLogout="true" />
    <main class="layout-main">
      <button v-if="compact && !siderCollapsed" class="layout-backdrop" aria-label="收起侧边栏" @click="toggleSider" />
      <aside class="layout-sider" :class="{ 'layout-sider--collapsed': siderCollapsed }">
        <SiderMenu
          :currentUser="currentUser"
          :collapsed="siderCollapsed"
          @toggle="toggleSider"
        />
      </aside>
      <section class="layout-content">
        <router-view></router-view>
      </section>
    </main>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import PageHeader from '../components/PageHeader'
import SiderMenu from '../components/SiderMenu'
import { useRouter } from 'vue-router';
import { getCurrentSessionRevision, getStoredCurrentUser, isAdminUser, resolveHomePath } from '../utils/currentUser'
import { checkUserSession } from '../api/TikuComponentApi'

export default {
  name: 'AdapterLayout',
  setup() {
    const router = useRouter()
    const compactViewport = typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 960px)')
      : null
    const siderCollapsed = ref(Boolean(compactViewport?.matches))
    const compact = ref(Boolean(compactViewport?.matches))
    const currentUser = computed(() => getStoredCurrentUser())
    const toggleSider = () => {
      siderCollapsed.value = !siderCollapsed.value
    }
    const syncSiderToViewport = (event) => {
      compact.value = event.matches
      siderCollapsed.value = event.matches
    }
    let sessionTimer = null
    let sessionRequest = null
    let sessionChecksStopped = false
    const stopSessionChecks = () => {
      sessionChecksStopped = true
      if (sessionTimer !== null) window.clearInterval(sessionTimer)
      sessionTimer = null
      window.removeEventListener('focus', checkSession)
      document.removeEventListener('visibilitychange', checkSession)
      sessionRequest?.abort()
    }
    const checkSession = async () => {
      if (sessionChecksStopped || sessionRequest || document.visibilityState === 'hidden' || !getStoredCurrentUser()) return
      const controller = new AbortController()
      const revision = getCurrentSessionRevision()
      sessionRequest = controller
      try {
        const response = await checkUserSession(controller.signal)
        if ((response?.status === 401 || response?.status === 403) && revision === getCurrentSessionRevision()) stopSessionChecks()
      } catch {
        // Temporary network failures are retried by the next heartbeat.
      } finally {
        if (sessionRequest === controller) sessionRequest = null
      }
    }
    onMounted(() => {
      if (compactViewport?.addEventListener) compactViewport.addEventListener('change', syncSiderToViewport)
      else compactViewport?.addListener?.(syncSiderToViewport)
      if (currentUser.value) {
        sessionTimer = window.setInterval(checkSession, 30_000)
        window.addEventListener('focus', checkSession)
        document.addEventListener('visibilitychange', checkSession)
        void checkSession()
      }
    })
    onBeforeUnmount(() => {
      stopSessionChecks()
      if (compactViewport?.removeEventListener) compactViewport.removeEventListener('change', syncSiderToViewport)
      else compactViewport?.removeListener?.(syncSiderToViewport)
    })
    if (!currentUser.value) {
      router.push('/login')
      return {
        currentUser,
        compact,
        siderCollapsed,
        toggleSider,
      }
    }

    const routePath = String(router.currentRoute.value?.path || '')
    if (!isAdminUser(currentUser.value)) {
      const adminOnlyPrefixes = [
        '/adapter/component',
        '/adapter/import',
        '/adapter/userlist',
        '/adapter/loglist',
        '/adapter/search-account-config',
        '/adapter/ai-search-config',
        '/adapter/system-settings',
        '/adapter/extension-updates',
      ]
      if (adminOnlyPrefixes.some(prefix => routePath.startsWith(prefix))) {
        router.replace(resolveHomePath(currentUser.value))
      }
    }

    return {
      currentUser,
      compact,
      siderCollapsed,
      toggleSider,
    }
  },
  components: {
    PageHeader,
    SiderMenu
  }
}
</script>

<style scoped>
.layout-shell {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--console-canvas);
}

.layout-main {
  position: relative;
  min-height: 0;
  flex: 1;
  width: 100%;
  display: flex;
}

.layout-sider {
  width: 260px;
  flex-shrink: 0;
  min-height: 0;
  background: var(--console-surface);
  border-right: 1px solid var(--console-border);
  transition: width 160ms ease;
}

.layout-sider--collapsed {
  width: 64px;
}

.layout-content {
  flex: 1;
  min-width: 0;
  padding: 24px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
}
.layout-backdrop { position: absolute; inset: 0; z-index: 20; border: 0; background: rgb(23 43 39 / 15%); cursor: pointer; }

@media (max-width: 960px) {
  .layout-sider {
    position: absolute;
    inset: 0 auto 0 0;
    width: 260px;
    z-index: 30;
    box-shadow: 8px 0 24px rgb(23 43 39 / 8%);
  }

  .layout-sider.layout-sider--collapsed {
    width: 64px;
    box-shadow: none;
  }

  .layout-content {
    padding: 16px;
    margin-left: 64px;
  }
}
</style>
